import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import CreatorApplicationLayout from "../../../layouts/CreatorApplicationLayout";
import styles from "./MemberOnboarding.module.css";
import MemberStepper from "./components/MemberStepper";
import Step1MemberBasicInfo from "./steps/Step1MemberBasicInfo";
import Step2MemberEmailVerification from "./steps/Step2MemberEmailVerification";
import Step3MemberPhoneVerification from "./steps/Step3MemberPhoneVerification";
import Step4MemberProfile from "./steps/Step4MemberProfile";
import Step4_1MemberMoreAboutYou from "./steps/Step4_1MemberMoreAboutYou";
import Step4_2MemberLifestyleProfile from "./steps/Step4_2MemberLifestyleProfile";
import Step5MemberChooseMembership from "./steps/Step5MemberChooseMembership";
import { clearTokens } from "../../../utils/tokens";
import { queryClient } from "../../../query/queryClient";
import { logoutUser } from "../../../redux/modules/userSlice";

export default function MemberOnboarding() {
  const historyRouter = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<{
    step?: number;
    email?: string;
    autoSendOtp?: boolean;
    initialStepState?: "SELECT_METHOD" | "ENTER_OTP";
    phoneNumber?: string;
  }>();

  // If user has already completed all onboarding steps (email, phone, profile), redirect to home
  useEffect(() => {
    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    const isEmailOk = u?.emailVerified === true || u?.validated === true;
    const isPhoneOk = u?.phoneVerified === true || Boolean(u?.phoneNumber);
    const isProfileOk = u?.profileCompletion === true || Boolean(u?.aboutMe) || u?.isOnboarding === true || Boolean(u?.currentSubscriptionTier);

    console.log("[MemberOnboarding] Checking if user already finished onboarding:", {
      isEmailOk,
      isPhoneOk,
      isProfileOk,
      isOnboarding: u?.isOnboarding,
      willRedirectHome: isEmailOk && isPhoneOk && isProfileOk && u?.isOnboarding === true
    });

    if (isEmailOk && isPhoneOk && isProfileOk && u?.isOnboarding === true) {
      console.log("[MemberOnboarding] All steps verified and isOnboarding=true -> Redirecting to home '/'");
      historyRouter.replace("/");
    }
  }, [historyRouter]);

  const getMinAllowedStep = () => {
    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    if (!u) return 0;
    if (u.profileCompletion === true || u.aboutMe) return 4;
    if (u.phoneVerified === true || u.phoneNumber) return 3;
    if (u.emailVerified === true || u.validated === true) return 2;
    return 0;
  };

  const getMaxAllowedStep = () => {
    let u: any = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) u = JSON.parse(stored);
    } catch { }

    if (!u) return 0;
    // Cannot proceed beyond email verification until verified
    if (u.emailVerified !== true && u.validated !== true) return 1;
    // Cannot proceed beyond phone verification until verified
    if (u.phoneVerified !== true) return 2;
    // Cannot proceed beyond profile completion until completed
    if (u.profileCompletion !== true) return 3;
    // Once verified, allowed up to final membership tier selection
    return 6;
  };

  const getInitialStep = () => {
    if (typeof location.state?.step === "number") {
      console.log("[MemberOnboarding] Initial step resolved from location.state.step:", location.state.step);
      return location.state.step;
    }
    const params = new URLSearchParams(location.search);
    const stepParam = params.get("step");
    if (stepParam !== null) {
      const parsed = parseInt(stepParam, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 6) {
        console.log("[MemberOnboarding] Initial step resolved from URL query param ?step=" + parsed);
        return parsed;
      }
    }
    const calculatedMin = getMinAllowedStep();
    console.log("[MemberOnboarding] Initial step defaulting to getMinAllowedStep():", calculatedMin);
    return calculatedMin;
  };

  const minAllowedStep = getMinAllowedStep();
  const maxAllowedStep = getMaxAllowedStep();
  const rawInitialStep = getInitialStep();
  const initialStep = Math.min(Math.max(rawInitialStep, minAllowedStep), maxAllowedStep);

  console.log("[MemberOnboarding:Init]", {
    rawInitialStep,
    minAllowedStep,
    maxAllowedStep,
    clampedInitialStep: initialStep,
    locationState: location.state,
  });

  const [activeStep, setActiveStep] = useState(initialStep);
  const [history, setHistory] = useState<number[]>([initialStep]);
  const [registeredEmail, setRegisteredEmail] = useState(() => {
    if (location.state?.email) return location.state.email;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        if (u.email) return u.email;
      }
    } catch { }
    return "";
  });
  const [autoSendOtp, setAutoSendOtp] = useState<boolean>(() => {
    if (location.state?.autoSendOtp !== undefined) return location.state.autoSendOtp;
    return initialStep === 1;
  });
  const [registeredPhone, setRegisteredPhone] = useState(() => {
    if (location.state?.phoneNumber) return location.state.phoneNumber;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        if (u.phoneNumber) return u.phoneNumber;
      }
    } catch { }
    return "";
  });
  const [phoneSkipped, setPhoneSkipped] = useState(false);
  const [memberProfileData, setMemberProfileData] = useState<Record<string, any>>({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeStep]);

  // Guard: clamp activeStep so user can never access steps already verified
  useEffect(() => {
    const minStep = getMinAllowedStep();
    console.log(`[MemberOnboarding:ClampCheck] activeStep=${activeStep}, minStep=${minStep}`);
    if (activeStep < minStep) {
      console.warn(`[MemberOnboarding:ClampTriggered] activeStep (${activeStep}) < minStep (${minStep}). Clamping to minStep ${minStep}.`);
      setActiveStep(minStep);
      setHistory([minStep]);
    }
  }, [activeStep]);

  const handleNext = (param?: number | any) => {
    console.log(`[MemberOnboarding:handleNext] Called from activeStep=${activeStep} with param:`, param);
    if (param && typeof param === "object") {
      if (param.email) {
        setRegisteredEmail(param.email);
      }
      if (param.phoneNumber) {
        setRegisteredPhone(param.phoneNumber);
      }
      if (param.phoneSkipped !== undefined) {
        setPhoneSkipped(param.phoneSkipped);
      }
      setMemberProfileData((prev) => {
        const updated = { ...prev, ...param };
        console.log("[MemberOnboarding:handleNext] Updated memberProfileData:", updated);
        return updated;
      });
    }
    const inc = typeof param === "number" ? param : 1;
    if (activeStep === 6) {
      console.log("[MemberOnboarding:handleNext] Final step 6 completed! Redirecting to '/'");
      historyRouter.push("/");
      return;
    }
    const nextStep = Math.min(activeStep + inc, 6);
    console.log(`[MemberOnboarding:handleNext] Moving to nextStep=${nextStep}`);
    setActiveStep(nextStep);
    setHistory((h) => [...h, nextStep]);
  };

  const handleBack = () => {
    const minStep = getMinAllowedStep();
    console.log(`[MemberOnboarding:handleBack] Called. activeStep=${activeStep}, minStep=${minStep}, history=`, history);
    if (activeStep <= minStep) {
      console.warn(`[MemberOnboarding:handleBack] Cannot go back past minStep (${minStep}). Blocked.`);
      return;
    }
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const targetStep = newHistory[newHistory.length - 1];
      if (targetStep >= minStep) {
        console.log(`[MemberOnboarding:handleBack] Navigating back to history step=${targetStep}`);
        setActiveStep(targetStep);
        setHistory(newHistory);
        return;
      }
    }
    if (activeStep - 1 >= minStep) {
      console.log(`[MemberOnboarding:handleBack] Navigating back to activeStep - 1 = ${activeStep - 1}`);
      setActiveStep(activeStep - 1);
      setHistory([activeStep - 1]);
    } else {
      console.log("[MemberOnboarding:handleBack] Back reached beginning -> Navigating to /login");
      historyRouter.push("/login");
    }
  };

  const currentMinStep = getMinAllowedStep();

  const renderStep = () => {
    console.log(`[MemberOnboarding:renderStep] Rendering step index ${activeStep} (currentMinStep=${currentMinStep})`);
    switch (activeStep) {
      case 0:
        return <Step1MemberBasicInfo onNext={handleNext} />;
      case 1:
        return (
          <Step2MemberEmailVerification
            onNext={handleNext}
            onBack={handleBack}
            email={registeredEmail}
            autoSendOtp={autoSendOtp}
            canGoBack={currentMinStep < 1}
          />
        );
      case 2:
        return (
          <Step3MemberPhoneVerification
            onNext={handleNext}
            onBack={handleBack}
            canGoBack={currentMinStep < 2}
            initialStepState={phoneSkipped ? "SELECT_METHOD" : location.state?.initialStepState}
            initialPhoneNumber={registeredPhone || location.state?.phoneNumber}
            phoneNumber={registeredPhone}
          />
        );
      case 3:
        return (
          <Step4MemberProfile
            onNext={handleNext}
            onBack={handleBack}
            savedData={memberProfileData}
            canGoBack={currentMinStep < 3}
          />
        );
      case 4:
        return (
          <Step4_1MemberMoreAboutYou
            onNext={handleNext}
            onBack={handleBack}
            savedData={memberProfileData}
            canGoBack={currentMinStep < 4}
          />
        );
      case 5:
        return <Step4_2MemberLifestyleProfile onNext={handleNext} onBack={handleBack} profileData={memberProfileData} />;
      case 6:
        return <Step5MemberChooseMembership onNext={handleNext} onBack={handleBack} />;
      default:
        return null;
    }
  };

  const isEarlyStep = activeStep === 0 || activeStep === 1;

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isEarlyStep) {
      e.preventDefault();
      clearTokens();
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        localStorage.removeItem("role");
        localStorage.removeItem("hasRegisteredOrLoggedIn");
      } catch (err) {
        console.warn("Error clearing localStorage:", err);
      }
      queryClient.clear();
      dispatch(logoutUser());
      historyRouter.push("/");
    }
  };

  return (
    <CreatorApplicationLayout
      logoTarget={isEarlyStep ? "/" : "/member-onboarding-step"}
      onLogoClick={handleLogoClick}
    >
      <div className={styles.container}>

        {/* Stepper Component */}
        <MemberStepper activeStep={activeStep} />

        {/* Dynamic Form Step */}
        {renderStep()}

      </div>
    </CreatorApplicationLayout>
  );
}
